import {useHistory, useParams} from "react-router";

import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {useQuery} from "@khan/data-gql";
import {CoeditorContextProvider} from "@khan/guide-ui";
import {type ToolPersona} from "@khan/khanmigo-tools";

import PageRoot from "#app-shell-package/page-root.tsx";
import HeaderOnlyPageTemplate from "#app-shell-package/page-templates/header-only-page-template.tsx";
import {getToolsByConfigName} from "#coach-dashboard-package/khanmigo/ai-tools-list.ts";
import {TEACHER_TOOLS_FETCH} from "#coach-dashboard-package/khanmigo/ai-tools-queries.ts";
import {TEACHER_KHANMIGO_TOOLS_PATH} from "#coach-dashboard-package/khanmigo/path-helpers.ts";
import {createResumeDocumentHref} from "#coach-dashboard-package/resource-tab/utils.ts";
import useTeacherAccessPermissionsQuery, {
    type TeacherAccessPermissionsResult,
} from "#coach-dashboard-package/teacher-access-permissions/queries/use-teacher-access-permissions-query.ts";
import useProfile from "#data-access/use-profile.ts";

import {withStrings} from "./locales/teacher-document-page/load-strings.tsx";
import {COEDITING_DOCUMENT_QUERY} from "./queries.ts";
import TeacherDocumentView from "./teacher-document-view.tsx";

const toolsByConfigName = getToolsByConfigName();

// TO DO (bcdirito):
// Rename this component since it's used by Teachers && Parents now
function TeacherDocumentPage() {
    const {documentID} = useParams<{documentID: string}>();
    const history = useHistory();
    const profileResult = useProfile();

    const [coeditingDocumentResult] = useQuery(COEDITING_DOCUMENT_QUERY, {
        variables: {documentID: documentID},
        skip: !documentID,
    });

    const [teacherToolsResult] = useQuery(TEACHER_TOOLS_FETCH, {
        skip: !documentID,
    });

    const teacherAccessPermissionsResult: TeacherAccessPermissionsResult =
        useTeacherAccessPermissionsQuery({skip: !documentID});

    if (
        profileResult.status === "loading" ||
        teacherToolsResult.status === "loading" ||
        coeditingDocumentResult.status === "loading" ||
        teacherAccessPermissionsResult.status === "loading"
    ) {
        return (
            <PageRoot
                template={HeaderOnlyPageTemplate}
                settings={{
                    footer: false,
                }}
            >
                <CircularSpinner size="medium" />
            </PageRoot>
        );
    }

    const isQuerySuccess =
        teacherToolsResult.status === "success" &&
        coeditingDocumentResult.status === "success";
    if (
        !documentID ||
        !isQuerySuccess ||
        !coeditingDocumentResult.data.coeditingDocument ||
        !(
            coeditingDocumentResult.data.coeditingDocument.configName in
            toolsByConfigName
        )
    ) {
        history.push(TEACHER_KHANMIGO_TOOLS_PATH);
        return null;
    }

    const kaid =
        profileResult.status === "success"
            ? profileResult.profile?.get("kaid") || ""
            : "";

    const coeditingDocument = coeditingDocumentResult.data.coeditingDocument;
    const tool = toolsByConfigName[coeditingDocument.configName];
    if (coeditingDocument.ownerKaid === kaid) {
        history.push(
            createResumeDocumentHref(
                tool.urlSlug,
                coeditingDocument.threadId,
                coeditingDocument.persona as ToolPersona,
            ),
        );
        return null;
    }

    const isKhanmigoEligible =
        teacherAccessPermissionsResult.status === "success" &&
        teacherAccessPermissionsResult.data.isEligibleForKhanmigoTeacherTools;

    const accessibleToolConfigNames =
        teacherToolsResult.data.aiGuideTeacherTools.map(
            (tool) => tool.configName,
        );

    return (
        <PageRoot
            template={HeaderOnlyPageTemplate}
            settings={{
                title: tool.title,
                footer: false,
            }}
        >
            <CoeditorContextProvider>
                <TeacherDocumentView
                    tool={tool}
                    accessibleToolConfigNames={accessibleToolConfigNames}
                    isKhanmigoEligible={isKhanmigoEligible}
                    documentID={documentID}
                    snapshotID={coeditingDocument.latestSnapshot.id}
                    snapshot={{
                        title: coeditingDocument.title,
                        prefix: coeditingDocument.latestSnapshot.text.prefix,
                        selection:
                            coeditingDocument.latestSnapshot.text.selection,
                        suffix: coeditingDocument.latestSnapshot.text.suffix,
                    }}
                    persona={coeditingDocument.persona as ToolPersona}
                />
            </CoeditorContextProvider>
        </PageRoot>
    );
}

export default withStrings(TeacherDocumentPage);
